/**
 * Candidate channels — the rules for which channel carries a message to a candidate
 * (docs/candidate-communications-experience.md §9). Pure: the server dispatcher and the
 * recruiter settings share them.
 */
import { CandidateMessageIntent, MessageTransport, OFFER_DECISION_BRANCH, TouchpointAnchor, } from './clientServerApi';
export const CANDIDATE_CHANNELS = [
    MessageTransport.Sms,
    MessageTransport.WhatsApp,
    MessageTransport.Email,
];
export const CHANNEL_LABELS = {
    [MessageTransport.Sms]: 'SMS',
    [MessageTransport.WhatsApp]: 'WhatsApp',
    [MessageTransport.Email]: 'Email',
};
export const DEFAULT_CANDIDATE_CHANNELS_CONFIG = {
    order: [MessageTransport.Sms],
};
/** Intents that ask the candidate for an answer; only channels Stanor reads replies on may carry them. */
const INTERACTIVE_INTENTS = new Set([
    CandidateMessageIntent.ScreeningQuestion,
    CandidateMessageIntent.ConversationReply,
    CandidateMessageIntent.ResponseFollowUp,
    CandidateMessageIntent.InterviewScheduling,
    CandidateMessageIntent.Offer,
    CandidateMessageIntent.RecruiterMessage,
]);
/** Channels whose replies reach Stanor's conversation engine. Email replies aren't read yet. */
const REPLY_CHANNELS = new Set([
    MessageTransport.Sms,
    MessageTransport.WhatsApp,
]);
export function isInteractiveIntent(intent) {
    return INTERACTIVE_INTENTS.has(intent);
}
/** Replies on several channels can't be kept in one conversation, so only these may go on every channel. */
export function canSendOnAllChannels(intent) {
    return !isInteractiveIntent(intent);
}
/** What an interview-sequence message is: a confirmation or reminder, or a decision (an offer needs a reply). */
export function touchpointMessageIntent(touchpoint) {
    if (touchpoint.anchor !== TouchpointAnchor.RecruiterDecision)
        return CandidateMessageIntent.InterviewReminder;
    return touchpoint.decisionBranch === OFFER_DECISION_BRANCH
        ? CandidateMessageIntent.Offer
        : CandidateMessageIntent.StatusUpdate;
}
/** The strategy an interview-sequence message may use: every channel only when it needs no reply. */
export function allowedChannelStrategy(strategy, touchpoint) {
    return strategy === 'all_selected' && canSendOnAllChannels(touchpointMessageIntent(touchpoint))
        ? 'all_selected'
        : 'first_available';
}
/**
 * Channels the platform can deliver on. SMS always works (Twilio, or the candidate app in test
 * delivery); WhatsApp once an admin switches it on after the Twilio/Meta setup; email has no
 * provider yet.
 */
export function platformChannelAvailability(features) {
    return {
        [MessageTransport.Sms]: true,
        [MessageTransport.WhatsApp]: features.enableCandidateWhatsApp === true,
        [MessageTransport.Email]: false,
    };
}
/** WhatsApp lets a business send free-form messages for 24 hours after the person last wrote. */
export const WHATSAPP_SESSION_MS = 24 * 60 * 60 * 1000;
/** Stop free-form messages a little early: queues and clocks must never push one past Meta's window. */
export const WHATSAPP_SESSION_MARGIN_MS = 10 * 60 * 1000;
export function isWhatsAppSessionOpen(candidate, nowMs) {
    const lastInbound = channelContactOf(candidate, MessageTransport.WhatsApp).lastInboundAtUtc;
    return lastInbound !== null && nowMs - lastInbound < WHATSAPP_SESSION_MS - WHATSAPP_SESSION_MARGIN_MS;
}
/**
 * Whether the candidate can still get replies somewhere: texts, or WhatsApp — where they opted in,
 * or wrote within the last 24 hours (WhatsApp allows replies in that window without an opt-in).
 */
export function hasOpenReplyChannel(candidate, nowMs) {
    const whatsApp = channelContactOf(candidate, MessageTransport.WhatsApp);
    const whatsAppOpen = whatsApp.optedOut === null &&
        (whatsApp.consent !== null || isWhatsAppSessionOpen(candidate, nowMs));
    return !candidate.smsOptedOut || whatsAppOpen;
}
/**
 * The channels a workspace actually uses, in order. The candidate-communications platform switch
 * also governs channel choice: while it's off, everything goes by SMS as before.
 */
export function effectiveChannelOrder(config, communicationsEnabled) {
    return communicationsEnabled ? [...config.order] : [MessageTransport.Sms];
}
/** The channels actually in use: turned on by the recruiter, set up on the platform, in order. Free plans only text. */
export function channelsInUse(config, available, communicationsEnabled, freePlan) {
    return effectiveChannelOrder(config, communicationsEnabled).filter((channel) => available[channel] && (!freePlan || channel === MessageTransport.Sms));
}
export function validateCandidateChannelsConfig(config) {
    if (!Array.isArray(config.order) || config.order.length === 0)
        return 'Turn on at least one channel';
    if (config.order.some((channel) => !CANDIDATE_CHANNELS.includes(channel)))
        return 'Unknown channel';
    if (new Set(config.order).size !== config.order.length)
        return 'Each channel can be listed only once';
    return null;
}
/**
 * Channels besides SMS an applicant may opt in to on the application form: set up on the platform
 * and turned on by the job's recruiter, while candidate communications are switched on.
 */
export function offeredConsentChannels(config, available, communicationsEnabled) {
    if (!communicationsEnabled)
        return [];
    return config.order.filter((channel) => channel !== MessageTransport.Sms && available[channel]);
}
/** When channels are saved, at least one must be set up — otherwise nothing would reach candidates. */
export function validateChannelsAvailable(config, available) {
    return config.order.some((channel) => available[channel]) ? null : 'Turn on at least one channel that is set up';
}
const NO_CHANNEL_CONTACT = Object.freeze({
    consent: null,
    optedOut: null,
    lastInboundAtUtc: null,
    unreachable: null,
});
/** A new, empty contact record for each additional channel. */
export function emptyChannelContacts() {
    return {
        [MessageTransport.WhatsApp]: { consent: null, optedOut: null, lastInboundAtUtc: null, unreachable: null },
        [MessageTransport.Email]: { consent: null, optedOut: null, lastInboundAtUtc: null, unreachable: null },
    };
}
/** The candidate's consent and opt-out state on WhatsApp or email (nothing granted when never set). */
export function channelContactOf(candidate, channel) {
    return candidate.channelContacts?.[channel] ?? NO_CHANNEL_CONTACT;
}
/** Which of the workspace's channels can carry this message to this candidate, in order. */
export function planCandidateChannels(input) {
    const channels = [];
    const skipped = [];
    for (const channel of input.order) {
        const reason = channelSkipReason(channel, input);
        if (reason === null) {
            channels.push(channel);
        }
        else {
            skipped.push({ channel, reason });
        }
    }
    // Keep a conversation where the candidate is having it (§9.7: the first nudge stays there too).
    const { activeChannel } = input;
    if (activeChannel !== null && isInteractiveIntent(input.intent) && channels.includes(activeChannel)) {
        return { channels: [activeChannel, ...channels.filter((channel) => channel !== activeChannel)], skipped };
    }
    return { channels, skipped };
}
function channelSkipReason(channel, input) {
    if (!input.available[channel])
        return 'not_set_up';
    if (input.freePlan && channel !== MessageTransport.Sms)
        return 'free_plan';
    if (isInteractiveIntent(input.intent) && !REPLY_CHANNELS.has(channel))
        return 'replies_unsupported';
    const { candidate } = input;
    if (channel === MessageTransport.Sms) {
        if (input.smsNeedsPhone && !candidate.phone)
            return 'no_destination';
        if (candidate.smsOptedOut)
            return 'opted_out';
        return !input.smsUsesCredit || input.remainingCredits > 0 ? null : 'no_credits';
    }
    const destination = channel === MessageTransport.Email ? candidate.email : candidate.phone;
    if (!destination)
        return 'no_destination';
    const contact = channelContactOf(candidate, channel);
    if (contact.optedOut)
        return 'opted_out';
    const repliesInSession = channel === MessageTransport.WhatsApp && input.whatsAppSessionOpen;
    if (!contact.consent && !repliesInSession)
        return 'no_consent';
    // Truthiness, not `!== null`: contact records saved before this field existed lack it.
    if (contact.unreachable && contact.unreachable.destination === destination)
        return 'unreachable';
    return channel === MessageTransport.WhatsApp && !input.whatsAppSendable ? 'template_required' : null;
}
export const FIRST_AVAILABLE_ROUTE = Object.freeze({ kind: 'first_available' });
/** The candidate agreed to this channel themselves — an open WhatsApp session alone doesn't count. */
export function hasOptedIn(candidate, channel) {
    // Candidates from before SMS consent was recorded have none.
    if (channel === MessageTransport.Sms)
        return candidate.consentApproved?.approved === true;
    return channelContactOf(candidate, channel).consent !== null;
}
/** The candidate asked us to stop messaging them on this channel. */
export function hasOptedOut(candidate, channel) {
    if (channel === MessageTransport.Sms)
        return Boolean(candidate.smsOptedOut);
    // Truthiness: contact records saved before every field existed may lack one.
    return Boolean(channelContactOf(candidate, channel).optedOut);
}
export function routeChannels(plan, route, optedIn) {
    switch (route.kind) {
        case 'first_available':
            return plan.channels.slice(0, 2);
        case 'alternate': {
            const other = plan.channels.find((channel) => channel !== route.from && optedIn(channel));
            if (other === undefined)
                return plan.channels.slice(0, 2);
            const fallback = plan.channels.includes(route.from)
                ? route.from
                : plan.channels.find((channel) => channel !== other);
            return fallback === undefined ? [other] : [other, fallback];
        }
        case 'only':
            return plan.channels.includes(route.channel) ? [route.channel] : [];
    }
}
/** "We haven't heard back by text…" */
const HEARD_BACK_ON = {
    [MessageTransport.Sms]: 'by text',
    [MessageTransport.WhatsApp]: 'on WhatsApp',
    [MessageTransport.Email]: 'by email',
};
/** "…so we're trying WhatsApp too." */
const TRYING = {
    [MessageTransport.Sms]: 'texting you',
    [MessageTransport.WhatsApp]: 'trying WhatsApp',
    [MessageTransport.Email]: 'emailing you',
};
/** Why a final nudge arrives on a new channel (§9.7: the message explains why they're getting it). */
export function alternateChannelNote(from, to) {
    return `We haven't heard back ${HEARD_BACK_ON[from]}, so we're ${TRYING[to]} too.`;
}
/** The message without its `alternateChannelNote`, for sending it again on another channel. */
export function withoutAlternateChannelNote(text) {
    for (const from of CANDIDATE_CHANNELS) {
        for (const to of CANDIDATE_CHANNELS) {
            const note = `${alternateChannelNote(from, to)}\n\n`;
            if (text.startsWith(note))
                return text.slice(note.length);
        }
    }
    return text;
}
function describeSkip(skip) {
    const label = CHANNEL_LABELS[skip.channel];
    switch (skip.reason) {
        case 'not_set_up': return `${label} isn't set up yet`;
        case 'free_plan': return `${label} isn't available on the Free plan`;
        case 'replies_unsupported': return `${label} can't carry messages that need a reply`;
        case 'no_destination':
            return skip.channel === MessageTransport.Email ? 'there\'s no email address' : 'there\'s no phone number';
        case 'opted_out': return `they opted out of ${label}`;
        case 'no_consent': return `they haven't opted in to ${label}`;
        case 'template_required': return `${label} needs an approved template to start the conversation`;
        case 'unreachable': return `${label} couldn't reach them`;
        case 'no_credits': return 'you\'re out of message credits';
    }
}
/** Recruiter-facing reason no channel could carry a message, e.g. "they opted out of SMS". */
export function describeChannelSkips(skipped) {
    if (skipped.length === 0)
        return 'no channel is turned on';
    return skipped.map(describeSkip).join('; ');
}
